import { useContext, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { BulbIcon } from 'foundations-assets/icons/components/BulbIcon';
import { Flex } from 'foundations-components/components/Flex';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { View } from 'foundations-components/components/View';
import { BreezeBadge } from 'trellis-ecosystem';
import { trackFavoriteRolloutButtonClick, TrackingScreen } from '../../app/utils/tracking';
import { utcToRolloutOffsetLocal } from '../../app/utils/dateUtils';
import { GATES } from '../../app/utils/constants';
import { categoryToSubscreenMap } from '../../app/utils/tracking/utils';
import { useSetQueryParams } from '../../app/hooks/useSetQueryParams';
import { FeedbackBtnClickTrackerContext, MeetingScheduleLinkClickTrackerContext } from '../contexts/Tracking';
import { useDateFieldForCategory } from '../hooks/useDateFieldForCategory';
import RolloutProductUpdateProducts from './RolloutProductUpdateProducts';
import RolloutProductUpdateDate from './RolloutProductUpdateDate';
import RolloutProductUpdateImpactTag from './RolloutProductUpdateImpactTag';
import RolloutProductUpdatesScheduledForReleaseTag from './RolloutProductUpdatesScheduledForReleaseTag';
import RolloutProductUpdateStatusTag from './RolloutProductUpdateStatusTag';
import ProductUpdatesBetaButton from './ProductUpdatesBetaButton';
import FeedbackButton from './FeedbackButton';
import GateWrapper from './utils/GateWrapper';
import InViewTrigger from './utils/InViewTrigger';
import { NewToYouContext } from './IndexPage';
import FavoriteRolloutIcon from './FavoriteRolloutIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const RolloutProductUpdateRow = ({
  productUpdate,
  category,
  isNewToYouTab,
  isRecommendedTab,
  unviewedPUIds,
  schedulePUViewed
}) => {
  const setQueryParams = useSetQueryParams();
  const {
    newToYouTimestamp
  } = useContext(NewToYouContext);
  const dateField = useDateFieldForCategory();
  const theme = useTheme();
  const isTrellis = isTrellisTheme(theme);
  const btnUseSecondary = isTrellis ? 'secondary' : 'tertiary-light';
  const [isHovered, setIsHovered] = useState(false);
  const {
    featureGroups,
    id,
    recommended,
    releaseDate,
    rollout: {
      enrollType,
      enrollmentStatus,
      gate,
      id: rolloutId,
      impactLevel,
      products,
      betaPaused,
      hasAiFeature
    },
    isFavorite,
    rolloutState,
    stageDate,
    title,
    untranslatedTitle,
    updatedAt
  } = productUpdate;
  const thirtyDaysAgo = utcToRolloutOffsetLocal(I18n.moment()).subtract(30, 'days');
  const isLiveWithinThirtyDays = rolloutState === 'LIVE' && releaseDate && utcToRolloutOffsetLocal(releaseDate).isAfter(thirtyDaysAgo);
  const showLiveFeedbackButton = isLiveWithinThirtyDays;
  const handleProductUpdateSelect = () => {
    setQueryParams({
      rollout: rolloutId.toString(),
      update: ''
    });
  };
  const wrapWithInViewTrigger = node => unviewedPUIds.has(id) ? /*#__PURE__*/_jsx(InViewTrigger, {
    onVisibilityChange: isVisible => {
      if (isVisible) {
        schedulePUViewed(id);
      }
    },
    children: node
  }, id) : node;
  return /*#__PURE__*/_jsxs("tr", {
    "data-test-id": "product-update-row",
    children: [/*#__PURE__*/_jsx("td", {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      children: wrapWithInViewTrigger( /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "100",
          children: [/*#__PURE__*/_jsx(Link, {
            "data-test-id": "product-update-title",
            onClick: handleProductUpdateSelect,
            children: /*#__PURE__*/_jsx(TruncateString, {
              matchContentWidth: true,
              "data-test-id": "product-update-title-text",
              children: title
            })
          }), /*#__PURE__*/_jsx(GateWrapper, {
            ungatedToAny: [GATES.NEW_TO_YOU_GATE],
            fallbackComponent: unviewedPUIds.has(id) && !isNewToYouTab && /*#__PURE__*/_jsx(Badge, {
              use: "new",
              children: updatedAt === stageDate ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.badges.updated"
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.badges.new"
              })
            }),
            children: newToYouTimestamp && stageDate >= newToYouTimestamp && !isNewToYouTab && updatedAt === stageDate && /*#__PURE__*/_jsx(Badge, {
              use: "new",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.badges.updated"
              })
            })
          }), /*#__PURE__*/_jsx(GateWrapper, {
            ungatedToAny: [GATES.NEW_TO_YOU_GATE],
            children: hasAiFeature && /*#__PURE__*/_jsx(View, {
              children: /*#__PURE__*/_jsx(BreezeBadge, {})
            })
          }), recommended && !isRecommendedTab && /*#__PURE__*/_jsx(Tooltip, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.sidebar.recommendedIconTooltip"
            }),
            children: /*#__PURE__*/_jsx(IconHolder, {
              size: "200",
              icon: BulbIcon,
              color: "icon-core-default",
              padding: "0"
            })
          }), /*#__PURE__*/_jsx(FavoriteRolloutIcon, {
            size: "200",
            rolloutId: rolloutId,
            productUpdateId: id,
            productUpdateTitle: title,
            isFavorite: isFavorite,
            isPUHovered: isHovered,
            onAddCompletedCb: () => {
              trackFavoriteRolloutButtonClick({
                screen: TrackingScreen.PRODUCT_UPDATES,
                subscreen: categoryToSubscreenMap[category !== null && category !== void 0 ? category : 'all'],
                origin: 'table',
                productUpdateId: `${id}`,
                action: 'add favorite'
              });
            },
            onRemoveCompletedCb: () => {
              trackFavoriteRolloutButtonClick({
                screen: TrackingScreen.PRODUCT_UPDATES,
                subscreen: categoryToSubscreenMap[category !== null && category !== void 0 ? category : 'all'],
                origin: 'table',
                productUpdateId: `${id}`,
                action: 'remove favorite'
              });
            }
          })]
        }), /*#__PURE__*/_jsx(RolloutProductUpdateStatusTag, {
          id: id,
          rolloutId: rolloutId,
          title: title,
          untranslatedTitle: untranslatedTitle,
          gate: gate,
          rolloutState: rolloutState,
          enrollmentStatus: enrollmentStatus,
          enrollType: enrollType,
          betaPaused: betaPaused
        }), /*#__PURE__*/_jsx(RolloutProductUpdatesScheduledForReleaseTag, {
          date: releaseDate,
          rolloutState: rolloutState
        })]
      }))
    }), /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(RolloutProductUpdateImpactTag, {
        impactLevel: impactLevel
      })
    }), /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(RolloutProductUpdateProducts, {
        rolloutProducts: products,
        featureGroups: featureGroups
      })
    }), /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(RolloutProductUpdateDate, {
        date: dateField === 'stageDate' ? stageDate : releaseDate ? utcToRolloutOffsetLocal(releaseDate).valueOf() : rolloutState === 'LIVE' || rolloutState === 'SUNSET' ? stageDate // default to stageDate if no releaseDate for LIVE or SUNSET
        : null
      })
    }), /*#__PURE__*/_jsx("td", {
      children: showLiveFeedbackButton ? /*#__PURE__*/_jsx(FeedbackBtnClickTrackerContext.Provider, {
        value: {
          screen: TrackingScreen.PRODUCT_UPDATES,
          subscreen: categoryToSubscreenMap[category !== null && category !== void 0 ? category : 'all'],
          origin: 'table',
          rolloutState: 'LIVE',
          untranslatedPUTitle: untranslatedTitle
        },
        children: /*#__PURE__*/_jsx(MeetingScheduleLinkClickTrackerContext.Provider, {
          value: {
            screen: TrackingScreen.PRODUCT_UPDATES,
            subscreen: categoryToSubscreenMap[category !== null && category !== void 0 ? category : 'all'],
            origin: 'table'
          },
          children: /*#__PURE__*/_jsx(FeedbackButton, {
            use: btnUseSecondary,
            size: "small",
            rolloutId: rolloutId,
            productUpdateId: id,
            productUpdateTitle: title
          })
        })
      }) : /*#__PURE__*/_jsx(ProductUpdatesBetaButton, {
        productUpdate: productUpdate,
        category: category
      })
    })]
  });
};
export default RolloutProductUpdateRow;